import React, { useRef, useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import { makeStyles } from 'tss-react/mui';

import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import type { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

export type QSegmentedTabsSize = 'small' | 'medium' | 'large' | 'huge';

interface QSegmentedTabsProps extends Omit<ToggleButtonGroupProps, 'size'> {
  elevated?: boolean;
  size?: QSegmentedTabsSize;
}

const useStyles = makeStyles<{ elevated: boolean; size: QSegmentedTabsSize }>()((theme, { elevated, size }) => ({
  qSegmentedTabs: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius * 3,
    backgroundColor: elevated ? theme.palette.background.layer1 : theme.palette.background.default,
    boxShadow: elevated ? theme.shadows[2] : theme.shadows[0],
  },
  toggleButton: {
    '&:hover': { backgroundColor: 'action.hover' },
    backgroundColor: 'transparent',
    ...(size === 'small' && {
      padding: theme.spacing(0.5),
      minHeight: 26,
      ...theme.typography.captionMedium,
    }),
    ...(size === 'medium' && {
      padding: theme.spacing(0.75),
      minHeight: 32,
      ...theme.typography.paragraphMedium,
    }),
    ...(size === 'large' && {
      padding: theme.spacing(1),
      minHeight: 36,
      ...theme.typography.bodyMedium,
    }),
    ...(size === 'huge' && {
      padding: theme.spacing(1.5),
      minHeight: 40,
      ...theme.typography.bodyMedium,
    }),
  },
  selected: {
    backgroundColor: 'transparent !important',
  },
  slider: {
    position: 'absolute',
    borderRadius: size === 'small' ? 6 : size === 'medium' ? 8 : size === 'large' ? 12 : 12,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  sliderWithTransition: {
    transition: 'left 0.2s ease-in-out, top 0.2s ease-in-out, width 0.2s ease-in-out, height 0.2s ease-in-out',
  },
}));

const QSegmentedTabs = ({
  className,
  children,
  onChange,
  elevated = false,
  size = 'medium',
  ...otherProps
}: QSegmentedTabsProps) => {
  const selectedToggleButtonRef = useRef<HTMLAnchorElement>(undefined);
  const { classes, cx } = useStyles({ elevated, size });
  const [updateMeasureRef] = useMeasure(); // todo this takes a while to render
  const [isMounted, setIsMounted] = useState(false);

  // Only pass size to ToggleButtonGroup if it's a standard MUI size (not 'huge')
  const muiSize = size === 'huge' ? undefined : size;

  // Enable transition only after initial mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <ToggleButtonGroup
      onChange={(e, val) => {
        if (val !== null) {
          // buttons can be enabled / disabled - we want to block null
          onChange?.(e, val);
        }
      }}
      {...otherProps}
      size={muiSize}
      exclusive
      className={cx(classes.qSegmentedTabs, className)}
      sharedcomponentid='QSegmentedTabs'
    >
      <Box
        id='slider'
        className={cx(classes.slider, isMounted && classes.sliderWithTransition)}
        left={selectedToggleButtonRef.current?.offsetLeft || 0}
        top={selectedToggleButtonRef.current?.offsetTop || 0}
        width={selectedToggleButtonRef.current?.clientWidth || 0}
        height={selectedToggleButtonRef.current?.clientHeight || 0}
      />

      {Array.isArray(children) &&
        children.map((toggleButton) => {
          return (
            <ToggleButton
              key={toggleButton?.key}
              disableRipple
              classes={{ selected: classes.selected, root: classes.toggleButton }}
              ref={(currentTarget) => {
                if (currentTarget && toggleButton.props.value === otherProps.value) {
                  updateMeasureRef(currentTarget);
                  selectedToggleButtonRef.current = currentTarget;
                }
              }}
              {...toggleButton.props}
            />
          );
        })}
    </ToggleButtonGroup>
  );
};
export default QSegmentedTabs;
